import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Loader2, ShieldCheck, CheckCircle2, KeyRound, ArrowRight } from "lucide-react";
import api from "@/lib/api";

/**
 * One-time admin promotion page.
 *
 * Use this once per environment to promote an email to is_admin=true without
 * touching Terminal. After your account is admin you'll never need this page
 * again — the 5-tap-logo modal handles ongoing admin access.
 *
 * URL: /admin-bootstrap (noindex, nofollow)
 */
export default function AdminBootstrapPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post(
        "/admin/users/promote",
        { email: email.trim(), is_admin: true },
        { headers: { "X-API-Key": apiKey.trim() } }
      );
      setDone(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 401) {
        setError("Invalid API key. Double-check the BLOG_API_KEY value from your production environment.");
      } else if (err?.response?.status === 404) {
        setError("No user with that email. Sign up first at /signup, then return here.");
      } else {
        setError(typeof d === "string" ? d : "Promotion failed. Check the key and email and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0D0C] text-[#F5F2EB] flex items-center justify-center px-4 py-12">
      <Helmet>
        <title>Admin bootstrap · AgentReply</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="w-full max-w-lg bg-[#1A1715] border border-[#D95B38]/30 p-8 md:p-10">
        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D95B38] mb-3 flex items-center gap-2">
          <ShieldCheck className="w-3 h-3" /> Admin bootstrap · one-time use
        </div>

        {done ? (
          <>
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 className="w-6 h-6 text-green-400" strokeWidth={1.8} />
              <h1 className="font-serif text-2xl leading-tight">You're an admin.</h1>
            </div>
            <p className="font-mono text-xs text-[#A39A8F] mb-6 leading-relaxed">
              <span className="text-[#E0D8CA]">{email}</span> now has admin access. Going forward, just tap the AgentReply
              logo <span className="text-[#F5F2EB]">5 times fast</span> from any page → continue with Google →
              ops dashboard. No need to come back here.
            </p>
            <button
              type="button"
              onClick={() => navigate("/dashboard/admin/health")}
              data-testid="admin-bootstrap-go-ops"
              className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] px-6 py-3 transition-colors btn-press flex items-center justify-center gap-2"
            >
              Open ops dashboard <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <p className="font-mono text-[10px] text-[#7A7068] mt-4 text-center">
              You can bookmark this page or delete it from your history — promotion is permanent.
            </p>
          </>
        ) : (
          <>
            <h1 className="font-serif text-2xl md:text-3xl leading-tight mb-3">Promote your account to admin.</h1>
            <p className="font-mono text-xs text-[#A39A8F] mb-6 leading-relaxed">
              Enter the email of the account you already signed up with (Google or email/password — doesn't matter) and your
              <span className="text-[#E0D8CA]"> BLOG_API_KEY</span> from your production environment variables.
              This sets <span className="text-[#E0D8CA]">is_admin = true</span> on your user row.
            </p>

            <form onSubmit={submit} className="space-y-5">
              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2">Email of account to promote</span>
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="bootstrap-email"
                  className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]"
                />
              </label>

              <label className="block">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A39A8F] block mb-2 flex items-center gap-2">
                  <KeyRound className="w-3 h-3" />
                  BLOG_API_KEY
                </span>
                <input
                  type="password"
                  required
                  value={apiKey}
                  onChange={(e) => setApiKey(e.target.value)}
                  placeholder="from your production env"
                  data-testid="bootstrap-api-key"
                  className="w-full bg-transparent border-b-2 border-[#F5F2EB]/20 font-mono text-sm py-2.5 focus:outline-none focus:border-[#D95B38]"
                />
                <span className="font-mono text-[10px] text-[#7A7068] block mt-2 leading-relaxed">
                  Find it in your Emergent deployment → Environment Variables → <span className="text-[#A39A8F]">BLOG_API_KEY</span>. Same key already in your <span className="text-[#A39A8F]">/app/backend/.env</span>.
                </span>
              </label>

              {error && (
                <div className="font-mono text-xs text-red-400 border border-red-500/30 bg-red-500/5 px-3 py-2" data-testid="bootstrap-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !email || !apiKey}
                data-testid="bootstrap-submit"
                className="w-full font-mono text-xs uppercase tracking-[0.25em] bg-[#D95B38] text-[#F5F2EB] hover:bg-[#c14d2e] disabled:opacity-60 px-6 py-3 transition-colors btn-press flex items-center justify-center gap-2"
              >
                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                {loading ? "Promoting…" : "Make me an admin →"}
              </button>
            </form>

            <p className="font-mono text-[10px] text-[#7A7068] mt-6 leading-relaxed">
              Not signed up yet? <Link to="/signup" className="text-[#D95B38] hover:underline">Sign up first</Link> with the
              account you want to make admin, then come back here.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
